import {createReducer} from 'redux-act';
import {isEqual} from 'lodash';
import {
  RC_CLEAR_ALL_CALCULATOR_DATA,
  RC_CLEAR_CALCULATOR_DATA,
  RC_FEES_AND_TAXES_CHECK,
  RC_GENERATE_ORDER_REFUND,
  RC_GENERATE_ORDER_REFUND_SUCCESS,
  RC_GET_ORDER,
  RC_GET_ORDER_SUCCESS,
  RC_HANDLE_CHECK,
  RC_LOCATION_FAULT_SELECT,
  RC_OPEN_CONFIRMATION_MODAL,
  RC_OPEN_RESULT_MODAL,
  RC_ORDER_INVOICE_ERROR,
  RC_QUANTITY_CHANGE,
  RC_SET_SUBTOTAL_PERCENT,
  RC_SET_LOADING,
  RC_SET_RESULT_LOADING,
  RC_ADJUSTMENTS_CHECK,
  RC_SET_FIXED_AMOUNT_REASON,
  RC_SET_CUSTOMER_REFUND_AMOUNT,
  RC_SET_LOCATION_REVERSE_AMOUNT,
  RC_SET_OTHER_FREE_TEXT,
  RC_GET_MERCHANTS,
  RC_SET_REIMBURSEMENT_RESPONSIBILITY,
  RC_SET_REIMBURSEMENT_REASON,
  RC_SET_REIMBURSEMENT_AMOUNT,
  RC_SET_OTHER_REIMBURSEMENT_FREE_TEXT,
  RC_GENERATE_ORDER_REIMBURSEMENT,
  RC_GENERATE_ORDER_REIMBURSEMENT_SUCCESS,
  RC_OPEN_REIMBURSEMENT_CONFIRMATION_MODAL,
  RC_OPEN_RESULT_REIMBURSEMENT_MODAL
} from 'store/refundsCalculator/action';

const getPreparedItems = (arr = [], parentPath = []) => {
  let resultArr = arr ? [...arr] : [];
  const previousPath = [...parentPath];
  if (resultArr.length > 0) {
    resultArr = resultArr.map((item) => {
      const path = [...previousPath, item.index];
      return {
        ...item,
        path,
        checked: false,
        selected: item.quantity,
        modifiers: getPreparedItems(item.modifiers, path)
      };
    });
  }
  return resultArr;
};

const unpackItems = (itemsArr = [], result = []) => {
  const resultArr = [];

  const unpack = (arr) => {
    arr.forEach((item) => {
      resultArr.push(item);
      if (item.modifiers.length > 0) {
        unpack(item.modifiers);
      }
    });
  };

  unpack(itemsArr);

  return resultArr;
};

const checkedAllElements = (currentItem, items, keyName, keyValue) => {
  // flat map modifiers in one dimension array
  const getModList = (mods) =>
    mods.flatMap((mod) => [mod, ...getModList(mod.modifiers)]);

  // base case
  return items.map((item) => ({
    ...item,
    [keyName]:
      getModList(currentItem.modifiers)
        .concat(currentItem)
        .findIndex((el) => isEqual(el.path, item.path)) !== -1
        ? keyValue
        : item[keyName]
  }));
};

const formatRestaurantName = (name) => {
  let result;
  if (name) {
    result = name?.toLowerCase().split(' ').join('-');
  }
  return result;
};

const initialState = () => ({
  orderData: {},
  partialRefCheckboxes: {
    tip: false,
    deliveryFee: false
  },
  invoiceIdError: null,
  endCustomerRefund: 0,
  restaurantReverse: 0,
  creditsRedeem: 0,
  creditsReward: 0,
  locationFault: 0,
  percent: 0,
  refundResponsibility: -1,
  openConfirmationModal: false,
  refunds: [],
  resultModal: false,
  resultModalInfo: {},
  loading: false,
  resultLoading: false,
  priceAdjustmentIds: [],
  deniedRefundTypes: [],
  fixedAmountReason: 0,
  customerRefundAmount: null,
  locationReverseAmount: null,
  otherFreeText: '',
  merchant: null,
  reimbursementResponsibility: -1,
  reimbursementAmount: 0,
  openConfirmationReimbursementModal: false,
  resultModalReinbersment: false,
  reimbursements: []
});

const refundsCalculatorReducer = createReducer(
  {
    [RC_GET_ORDER]: (state) => {
      return {
        ...state,
        orderData: {}
      };
    },
    [RC_GET_ORDER_SUCCESS]: (state, payload) => {
      const orderData = {
        id: payload.order?.id,
        invoiceId: payload.order?.invoiceId,
        orderType: payload.order?.type || 'N/A',
        orderDate: payload.order?.placedAt || 'N/A',
        orderNotes: payload.order?.notes || 'N/A',
        orderStatus: payload.order?.status || 'N/A',
        pickupDate: payload.order?.pickedUpAt || 'N/A',
        locationName: payload.order?.location?.name || 'N/A',
        customerName: payload.order?.customer?.name || 'N/A',
        customerEmail: payload.order?.customer?.email || 'N/A',
        deliveryId: payload.order?.delivery?.id || 'N/A',
        deliveryCompanyId: payload.order?.delivery?.externalId || 'N/A',
        deliveryCompany: payload.order?.delivery?.service || 'N/A',
        uberId: payload.order?.delivery?.uberId
          ? payload?.order?.delivery?.uberId?.substr(-5)
          : 'N/A',
        deliveryStatus: payload.order?.delivery?.status || 'N/A',
        statusReason: payload.order?.delivery?.statusReason || 'N/A',
        deliveryAddress: payload.order?.delivery?.address || 'N/A',
        deliveryInstructions: payload.order?.delivery?.instructions || 'N/A',
        dropOffDelay: payload.order?.delivery?.dropOffDelay || 'N/A',
        trackingLink: payload.order?.delivery?.trackingUrl,
        proofOfDelivery: payload.order?.delivery?.proof,
        subtotal: payload.order?.payment?.subtotalBeforeDiscount || 0,
        finalMarkup: payload.order?.payment?.finalMarkup || 0,
        discount: payload.order?.payment?.discount || 0,
        creditsRedeem: payload.order?.payment?.creditsRedeem || 0,
        creditsReward: payload.order?.payment?.creditsReward || 0,
        tax: payload.order?.payment?.tax || 0,
        serviceFee: payload.order?.payment?.serviceFee || 0,
        deliveryFee: payload.order?.payment?.deliveryFee || 0,
        tip: payload.order?.payment?.tip || 0,
        finalPayment: payload.order?.payment?.total || 0,
        items: unpackItems(getPreparedItems(payload.order?.orderItems)),
        paymentInfo: payload.order?.payment?.paymentIntentId || 'N/A',
        priceAdjustments: payload.order?.priceAdjustments || [],
        locationNetAmount: payload.order?.payment?.locationNetAmount,
        deliveryType: payload.order?.type,
        storefrontUrl: payload.order?.location?.storefrontUrl || null,
        locationId: payload.order?.location?.id,
        internalOrderStatus: payload?.order?.internalOrderStatus
      };

      return {
        ...state,
        orderData,
        refunds: payload.order?.refunds || [],
        reimbursements: payload.order?.reimbursements || [],
        invoiceIdError: payload.errorMessages || null,
        deniedRefundTypes: payload.deniedRefundTypes || [],
        loading: false
      };
    },
    [RC_ORDER_INVOICE_ERROR]: (state, payload) => {
      return {
        ...state,
        invoiceIdError: payload,
        loading: false
      };
    },
    [RC_HANDLE_CHECK]: (state, {index, checked}) => {
      const items = [...state.orderData.items];
      items[index] = {
        ...items[index],
        checked
      };

      let updatedItems = checkedAllElements(
        items[index],
        items,
        'checked',
        checked
      );

      if (!checked) {
        updatedItems = checkedAllElements(
          updatedItems[index],
          updatedItems,
          'selected',
          updatedItems[index].quantity
        );
      }

      return {
        ...state,
        orderData: {...state.orderData, items: updatedItems}
      };
    },
    [RC_QUANTITY_CHANGE]: (state, {index, value}) => {
      const items = [...state.orderData.items];
      items[index] = {
        ...items[index],
        selected: value
      };
      const updatedItems = checkedAllElements(
        items[index],
        items,
        'selected',
        value
      );

      return {
        ...state,
        orderData: {...state.orderData, items: updatedItems}
      };
    },
    [RC_FEES_AND_TAXES_CHECK]: (state, payload) => {
      const changedCheckboxes = {...state.partialRefCheckboxes};
      changedCheckboxes[payload] = !changedCheckboxes[payload];

      return {
        ...state,
        partialRefCheckboxes: changedCheckboxes
      };
    },
    [RC_LOCATION_FAULT_SELECT]: (state, payload) => {
      return {
        ...state,
        refundResponsibility: payload,
        locationFault: payload === 0 ? 'yes' : 'no'
      };
    },
    [RC_SET_SUBTOTAL_PERCENT]: (state, payload) => {
      return {
        ...state,
        percent: payload
      };
    },
    [RC_SET_FIXED_AMOUNT_REASON]: (state, payload) => {
      return {
        ...state,
        fixedAmountReason: payload
      };
    },
    [RC_SET_CUSTOMER_REFUND_AMOUNT]: (state, payload) => {
      return {
        ...state,
        customerRefundAmount: payload
      };
    },
    [RC_SET_LOCATION_REVERSE_AMOUNT]: (state, payload) => {
      return {
        ...state,
        locationReverseAmount: payload
      };
    },
    [RC_GENERATE_ORDER_REFUND]: (state) => {
      return {
        ...state,
        resultLoading: true
      };
    },
    [RC_GENERATE_ORDER_REFUND_SUCCESS]: (state, payload) => {
      return {
        ...state,
        endCustomerRefund: payload?.totalCustomerRefundAmount,
        restaurantReverse:
          payload?.totalReversedAmount || payload?.totalLocationReverseAmount,
        creditsRedeem: payload?.creditsRedeem,
        creditsReward: payload?.creditsReward,
        resultLoading: false
      };
    },
    [RC_GENERATE_ORDER_REIMBURSEMENT]: (state) => {
      return {
        ...state,
        resultLoading: true
      };
    },
    [RC_GENERATE_ORDER_REIMBURSEMENT_SUCCESS]: (state, payload) => {
      return {
        ...state,
        reimbursementAmount: payload?.orderLocationTransferAmount,
        resultLoading: false
      };
    },
    [RC_SET_REIMBURSEMENT_RESPONSIBILITY]: (state, payload) => {
      return {
        ...state,
        reimbursementResponsibility: payload
      };
    },
    [RC_SET_REIMBURSEMENT_AMOUNT]: (state, payload) => {
      return {
        ...state,
        reimbursementAmount: payload
      };
    },
    [RC_CLEAR_CALCULATOR_DATA]: (state) => {
      const clearedItems = [...state.orderData?.items].map((item) => {
        return {
          ...item,
          checked: false,
          selected: item.quantity
        };
      });

      return {
        ...state,
        endCustomerRefund: 0,
        restaurantReverse: 0,
        creditsRedeem: 0,
        creditsReward: 0,
        percent: 0,
        orderData: {...state.orderData, items: clearedItems},
        partialRefCheckboxes: {
          tip: false,
          deliveryFee: false
        },
        priceAdjustmentIds: [],
        fixedAmountReason: 0,
        customerRefundAmount: null,
        locationReverseAmount: null,
        otherFreeText: '',
        reimbursementAmount: 0
      };
    },
    [RC_CLEAR_ALL_CALCULATOR_DATA]: () => {
      return {
        ...initialState()
      };
    },
    [RC_OPEN_CONFIRMATION_MODAL]: (state, payload) => {
      return {
        ...state,
        openConfirmationModal: payload
      };
    },
    [RC_OPEN_REIMBURSEMENT_CONFIRMATION_MODAL]: (state, payload) => {
      return {
        ...state,
        openConfirmationReimbursementModal: payload
      };
    },
    [RC_OPEN_RESULT_MODAL]: (state, payload) => {
      return {
        ...state,
        resultModal: payload.isOpen,
        resultModalInfo: payload.info
      };
    },
    [RC_OPEN_RESULT_REIMBURSEMENT_MODAL]: (state, payload) => {
      return {
        ...state,
        resultModalInfo: payload.info,
        resultModalReinbersment: payload.isOpen
      };
    },
    [RC_SET_LOADING]: (state, payload) => {
      return {
        ...state,
        loading: payload
      };
    },
    [RC_SET_RESULT_LOADING]: (state, payload) => {
      return {
        ...state,
        resultLoading: payload
      };
    },
    [RC_ADJUSTMENTS_CHECK]: (state, payload) => {
      let result = state.priceAdjustmentIds;
      if (result.includes(payload)) {
        result = result.filter((el) => el !== payload);
      } else {
        result.push(payload);
      }
      return {
        ...state,
        priceAdjustmentIds: result
      };
    },
    [RC_SET_OTHER_FREE_TEXT]: (state, payload) => {
      return {
        ...state,
        otherFreeText: payload
      };
    },
    [RC_SET_OTHER_REIMBURSEMENT_FREE_TEXT]: (state, payload) => {
      return {
        ...state,
        otherFreeReimbursementText: payload
      };
    },
    [RC_GET_MERCHANTS]: (state, payload) => {
      return {
        ...state,
        merchant: payload
      };
    }
  },
  initialState()
);

export default refundsCalculatorReducer;
